import { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from 'context/AuthContext';
import { safeJSONParse } from 'utils/helpers/safeParsing';
import { useRegistryColumns } from 'utils/helpers/list/useRegistryColumns';
import type { ProductCardTab, ProductContextValue, ProductListColumnId, ProductProviderProps } from '../types';

const PRODUCT_COLUMN_DEFS: { id: ProductListColumnId; defaultVisible: boolean }[] = [
    { id: 'category', defaultVisible: false },
    { id: 'id', defaultVisible: false },
    { id: 'article', defaultVisible: false },
    { id: 'updated', defaultVisible: false },
];

const ProductContext = createContext<ProductContextValue | null>(null);

export const useProduct = (): ProductContextValue => useContext(ProductContext) as ProductContextValue;

export const useOptionalProduct = (): ProductContextValue | null => useContext(ProductContext);

export const ProductProvider = ({ children }: ProductProviderProps) => {
    const { auth } = useAuth();
    const orderName = `voltox-${auth.user.ID}-product-order`;
    const descName = `voltox-${auth.user.ID}-product-desc`;

    const [showEditProduct, setShowEditProduct] = useState<number | false>(false);
    const [productCardTab, setProductCardTab] = useState<ProductCardTab>('base');
    const [showPriceProduct, setShowPriceProduct] = useState<number | false>(false);
    const [showSpinner, setShowSpinner] = useState(false);
    const [listResetNonce, setListResetNonce] = useState(0);
    const [listRefreshNonce, setListRefreshNonce] = useState(0);
    const [productsOrder, setProductsOrder] = useState<string | false>(
        safeJSONParse(sessionStorage.getItem(orderName)) || '',
    );
    const [productsDesc, setProductsDesc] = useState(
        safeJSONParse(sessionStorage.getItem(descName)) === true,
    );
    const [productCheck, setProductCheck] = useState<number[]>([]);
    const { visible: listColumns, toggle: toggleListColumn } = useRegistryColumns(
        'registryColumns:product',
        PRODUCT_COLUMN_DEFS,
    );
    const showCategory = Boolean(listColumns.category);
    const showId = Boolean(listColumns.id);
    const showArticle = Boolean(listColumns.article);
    const showUpdated = Boolean(listColumns.updated);

    useEffect(() => {
        sessionStorage.setItem(orderName, JSON.stringify(productsOrder));
    }, [productsOrder, orderName]);

    useEffect(() => {
        sessionStorage.setItem(descName, JSON.stringify(productsDesc));
    }, [productsDesc, descName]);

    const triggerListReset = () => setListResetNonce((nonce) => nonce + 1);
    const triggerListRefresh = () => setListRefreshNonce((nonce) => nonce + 1);
    const openProductCard = (id: number, tab: ProductCardTab = 'base') => {
        setProductCardTab(tab);
        setShowEditProduct(id);
    };

    const values: ProductContextValue = {
        showEditProduct,
        setShowEditProduct,
        productCardTab,
        openProductCard,
        showPriceProduct,
        setShowPriceProduct,
        showSpinner,
        setShowSpinner,
        productsOrder,
        setProductsOrder,
        productsDesc,
        setProductsDesc,
        productCheck,
        setProductCheck,
        listResetNonce,
        triggerListReset,
        listRefreshNonce,
        triggerListRefresh,
        showCategory,
        showId,
        showArticle,
        showUpdated,
        toggleListColumn,
    };

    return <ProductContext.Provider value={values}>{children}</ProductContext.Provider>;
};
